import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { Relationship } from '../modules/GenomeWorkspaceView/LeftView/components/ConditionCard';
import type { ProviderType } from '../modules/GenomeWorkspaceView/LeftView/hooks/useAdvancedFilters';
import type { Condition, Group } from '../modules/GenomeWorkspaceView/LeftView/sections/AdvancedFiltersSection';

export interface AdvancedFilterProviderState {
  groups: Group[];
  relationshipBetweenGroups: Relationship;
}

interface AdvancedFiltersState {
  providers: Record<string, AdvancedFilterProviderState>;
  initializeProvider: (provider: ProviderType) => void;
  addGroup: (provider: ProviderType, condition?: Condition) => void;
  removeGroup: (provider: ProviderType, groupId: number) => void;
  addConditionToGroup: (provider: ProviderType, groupId: number, condition: Condition) => void;
  removeConditionFromGroup: (provider: ProviderType, groupId: number, conditionName?: string) => void;
  changeGroupRelationship: (provider: ProviderType, groupId: number, relationship: Relationship) => void;
  setRelationshipBetweenGroups: (provider: ProviderType, relationship: Relationship) => void;
  updateConditionValue: (provider: ProviderType, groupId: number, conditionName: string, newValue: string) => void;
  updateConditionType: (provider: ProviderType, groupId: number, conditionName: string, newType: string) => void;
  clearAllGroups: (provider: ProviderType) => void;
  setProviderState: (provider: ProviderType, state: AdvancedFilterProviderState) => void;
}

const getInitialProviderState = (): AdvancedFilterProviderState => ({
  groups: [],
  relationshipBetweenGroups: 'AND',
});

export const useAdvancedFiltersStore = create<AdvancedFiltersState>()(
  devtools(
    (set) => ({
      providers: {},
      initializeProvider: (provider) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: state.providers[provider] || getInitialProviderState(),
            },
          }),
          false,
          'advancedFilters/initializeProvider',
        ),
      addGroup: (provider, condition) =>
        set(
          (state) => {
            const newGroup: Group = {
              id: Date.now(),
              conditions: condition ? [condition] : [],
              relationship: 'AND',
            };
            return {
              providers: {
                ...state.providers,
                [provider]: {
                  ...state.providers[provider],
                  groups: [...state.providers[provider].groups, newGroup],
                },
              },
            };
          },
          false,
          'advancedFilters/addGroup',
        ),
      removeGroup: (provider, groupId) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: {
                ...state.providers[provider],
                groups: state.providers[provider].groups.filter((g) => g.id !== groupId),
              },
            },
          }),
          false,
          'advancedFilters/removeGroup',
        ),
      addConditionToGroup: (provider, groupId, condition) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: {
                ...state.providers[provider],
                groups: state.providers[provider].groups.map((g) =>
                  g.id === groupId ? { ...g, conditions: [...g.conditions, condition] } : g,
                ),
              },
            },
          }),
          false,
          'advancedFilters/addConditionToGroup',
        ),
      removeConditionFromGroup: (provider, groupId, conditionName) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: {
                ...state.providers[provider],
                groups: state.providers[provider].groups.map((g) =>
                  g.id === groupId
                    ? {
                        ...g,
                        conditions: g.conditions.filter((c: Condition) => c.name !== conditionName),
                      }
                    : g,
                ),
              },
            },
          }),
          false,
          'advancedFilters/removeConditionFromGroup',
        ),
      changeGroupRelationship: (provider, groupId, relationship) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: {
                ...state.providers[provider],
                groups: state.providers[provider].groups.map((g) => (g.id === groupId ? { ...g, relationship } : g)),
              },
            },
          }),
          false,
          'advancedFilters/changeGroupRelationship',
        ),
      setRelationshipBetweenGroups: (provider, relationship) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: {
                ...state.providers[provider],
                relationshipBetweenGroups: relationship,
              },
            },
          }),
          false,
          'advancedFilters/setRelationshipBetweenGroups',
        ),
      updateConditionValue: (provider, groupId, conditionName, newValue) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: {
                ...state.providers[provider],
                groups: state.providers[provider].groups.map((g) =>
                  g.id === groupId
                    ? {
                        ...g,
                        conditions: g.conditions.map((c: Condition) =>
                          c.name === conditionName ? { ...c, value: newValue } : c,
                        ),
                      }
                    : g,
                ),
              },
            },
          }),
          false,
          'advancedFilters/updateConditionValue',
        ),
      updateConditionType: (provider, groupId, conditionName, newType) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: {
                ...state.providers[provider],
                groups: state.providers[provider].groups.map((g) =>
                  g.id === groupId
                    ? {
                        ...g,
                        conditions: g.conditions.map((c: Condition) =>
                          c.name === conditionName ? { ...c, conditionType: newType } : c,
                        ),
                      }
                    : g,
                ),
              },
            },
          }),
          false,
          'advancedFilters/updateConditionType',
        ),
      clearAllGroups: (provider) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: {
                ...state.providers[provider],
                groups: [],
                relationshipBetweenGroups: 'AND',
              },
            },
          }),
          false,
          'clearAllGroups',
        ),
      setProviderState: (provider, newState) =>
        set(
          (state) => ({
            providers: {
              ...state.providers,
              [provider]: newState,
            },
          }),
          false,
          'setProviderState',
        ),
    }),
    {
      name: 'AdvancedFiltersStore',
      enabled: process.env.NODE_ENV === 'development',
    },
  ),
);
